import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import accountDeletionService from '../services/accountDeletionService'
import { AlertTriangle, Trash2, X, Eye, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'

export default function AccountDeletionModal({ isOpen, onClose }) {
  const { currentUser, userProfile, logout } = useAuth()
  const [step, setStep] = useState('preview') // 'preview', 'confirm', 'deleting', 'completed'
  const [preview, setPreview] = useState(null)
  const [deletionResults, setDeletionResults] = useState(null)
  const [confirmationText, setConfirmationText] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleGetPreview = async () => {
    if (!currentUser?.uid) return

    try {
      setIsLoading(true)
      const previewData = await accountDeletionService.getAccountDeletionPreview(currentUser.uid)
      setPreview(previewData)
      setStep('confirm')
    } catch (error) {
      console.error('Error getting deletion preview:', error)
      toast.error('Failed to get account deletion preview')
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmDeletion = async () => {
    if (!currentUser?.uid || !userProfile?.email) return
    if (confirmationText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" exactly as shown')
      return
    }

    try {
      setIsLoading(true)
      setStep('deleting')
      
      const results = await accountDeletionService.deleteUserAccount(
        currentUser.uid, 
        userProfile.email
      )
      
      setDeletionResults(results)
      setStep('completed')
      
      // Only logout if deletion was successful
      if (results.success) {
        setTimeout(() => {
          logout()
          toast.success('Account deleted successfully. You have been logged out.')
        }, 3000)
      } else {
        toast.error(`Account deletion failed. ${results.errors.length} errors occurred.`)
      }
      
    } catch (error) {
      console.error('Error deleting account:', error)
      toast.error('Failed to delete account. Please try again.')
      setStep('confirm')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setStep('preview')
    setPreview(null)
    setDeletionResults(null)
    setConfirmationText('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={handleClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                {step === 'preview' && 'Delete Account'}
                {step === 'confirm' && 'Confirm Account Deletion'}
                {step === 'deleting' && 'Deleting Account...'}
                {step === 'completed' && 'Account Deleted'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 'preview' && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Warning: This action cannot be undone
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>Deleting your account will permanently remove:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>Your profile and personal information</li>
                          <li>All alerts you've created</li>
                          <li>All scheduled alerts and calendar events</li>
                          <li>Your group memberships and subscriptions</li>
                          <li>Your notification preferences</li>
                          <li>All your data from the platform</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex">
                    <Eye className="h-5 w-5 text-blue-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Preview Deletion
                      </h3>
                      <p className="mt-1 text-sm text-blue-700">
                        Click below to see exactly what will be deleted from your account.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleClose}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGetPreview}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        <span>Preview Deletion</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {step === 'confirm' && preview && (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <h3 className="text-sm font-medium text-yellow-800 mb-2">
                    Deletion Preview
                  </h3>
                  <div className="text-sm text-yellow-700">
                    <p className="mb-2">The following data will be permanently deleted:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {Object.entries(preview.collections).map(([collection, count]) => (
                        <li key={collection}>
                          <span className="font-medium">{collection}:</span> {count} document{count !== 1 ? 's' : ''}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 font-medium">
                      Total: {preview.totalDocuments} document{preview.totalDocuments !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h3 className="text-sm font-medium text-red-800 mb-2">
                    Final Confirmation Required
                  </h3>
                  <p className="text-sm text-red-700 mb-3">
                    To confirm account deletion, type <strong>DELETE MY ACCOUNT</strong> in the box below:
                  </p>
                  <input
                    type="text"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    placeholder="Type: DELETE MY ACCOUNT"
                    className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setStep('preview')}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleConfirmDeletion}
                    disabled={isLoading || confirmationText !== 'DELETE MY ACCOUNT'}
                    className="flex-1 px-4 py-2 bg-red-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Deleting...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" />
                        <span>Delete Account</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {step === 'deleting' && (
              <div className="text-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
                <h3 className="text-lg font-medium text-gray-900">Deleting Your Account</h3>
                <p className="text-sm text-gray-600">
                  Please wait while we permanently delete all your data from our systems...
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500">
                    This process may take a few moments depending on the amount of data.
                  </p>
                </div>
              </div>
            )}

            {step === 'completed' && deletionResults && (
              <div className="text-center space-y-4">
                {deletionResults.success ? (
                  <>
                    <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
                    <h3 className="text-lg font-medium text-gray-900">Account Successfully Deleted</h3>
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <h4 className="text-sm font-medium text-green-800 mb-2">Deletion Summary</h4>
                      <div className="text-sm text-green-700">
                        <p>Total documents deleted: <strong>{deletionResults.totalDocumentsDeleted}</strong></p>
                        {deletionResults.errors.length > 0 && (
                          <p className="mt-2 text-yellow-600">
                            Warnings: {deletionResults.errors.length} (non-critical)
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      You will be automatically logged out in a few seconds.
                    </p>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-12 w-12 text-red-600 mx-auto" />
                    <h3 className="text-lg font-medium text-gray-900">Account Deletion Failed</h3>
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 text-left">
                      <h4 className="text-sm font-medium text-red-800 mb-2">Deletion Results</h4>
                      <div className="text-sm text-red-700">
                        <p>Documents deleted: <strong>{deletionResults.totalDocumentsDeleted}</strong></p>
                        <p className="mt-2">Errors encountered: <strong>{deletionResults.errors.length}</strong></p>
                        {deletionResults.errors.length > 0 && (
                          <div className="mt-3">
                            <p className="font-medium">Error Details:</p>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              {deletionResults.errors.slice(0, 5).map((error, index) => (
                                <li key={index}>
                                  <span className="font-medium">{error.step}:</span> {error.error}
                                </li>
                              ))}
                              {deletionResults.errors.length > 5 && (
                                <li>... and {deletionResults.errors.length - 5} more errors</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Your account was not fully deleted. Please contact support or try again.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
